import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { LayoutGrid, TrendingDown, TrendingUp, Repeat, Landmark, Info, SlidersHorizontal, Anchor } from 'lucide-react';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import MultiSelectDropdown from '../components/UI/MultiSelectDropdown';
import StrategyToolsDropdown from '../components/Strategies/StrategyToolsDropdown';
import SymbolSearch from '../components/StrategyBuilder/SymbolSearch';
import CandlestickChart from '../components/StrategyBuilder/CandlestickChart';
import CapitalRequirements from '../components/StrategyBuilder/CapitalRequirements';
import MobileStrategyBuilder from '../components/StrategyBuilder/MobileStrategyBuilder';
import CollapsibleAccountInfo from '../components/StrategyBuilder/CollapsibleAccountInfo';
import StrategyDetailModal from '../components/StrategyBuilder/StrategyDetailModal';
import NotificationPrefsModal from '../components/Strategies/NotificationPrefsModal';
import ValidationModal from '../components/StrategyBuilder/ValidationModal';
import { calculateRequiredCapital } from '@/lib/calculateRequiredCapital';
import { strategiesAPI, accountAPI, brokerAPI, plannedOrdersAPI } from '../services/api';
import { PageTransition, StaggerContainer, AnimatedItem } from '@/components/motion';
import useContainerMobile from '../hooks/useContainerMobile';

const strategyTypes = [
  {
    id: 'grid',
    name: 'Grid Trading',
    description: 'Automatically buy low and sell high across preset price levels. Ideal for sideways markets with predictable oscillations.',
    icon: LayoutGrid,
    parameters: [
      { name: 'symbol', label: 'Symbol', type: 'text', default: 'AAPL', required: true },
      { name: 'gridLevels', label: 'Grid Levels', type: 'number', default: 10, required: true },
      { name: 'gridSpacing', label: 'Grid Spacing (%)', type: 'number', default: 1, step: 0.1, required: true },
      { name: 'orderSize', label: 'Order Size ($)', type: 'number', default: 100, required: true },
      { name: 'upperBound', label: 'Upper Bound ($)', type: 'number', default: 185.3, required: true },
      { name: 'lowerBound', label: 'Lower Bound ($)', type: 'number', default: 170, required: true },
      { name: 'minSellReturnPercent', label: 'Min Sell Return (%)', type: 'number', default: 0.5, step: 0.1, required: false },
      { name: 'marketSellMode', label: 'Market Sell Mode', type: 'select', options: [{ value: 'incremental', label: 'Incremental' }, { value: 'full_close', label: 'Full Close' }], default: 'incremental', required: false },
    ],
    detailData: {
      howItWorks: [
        'Places buy and sell orders at predefined price intervals within a range',
        'Profits from price oscillations between upper and lower bounds',
        'Automatically resets orders as they get filled',
        'Works best in sideways markets with predictable volatility',
      ],
      bestMarkets: ['Ranging', 'Sideways', 'Stocks', 'Crypto'],
      tradeFlow: {
        pricePath: 'M 0,110 L 50,75 L 100,150 L 150,65 L 200,145 L 250,80 L 300,155 L 350,70 L 400,110',
        markers: [
          { x: 100, y: 150, type: 'buy' },
          { x: 150, y: 65, type: 'sell' },
          { x: 200, y: 145, type: 'buy' },
          { x: 250, y: 80, type: 'sell' },
          { x: 300, y: 155, type: 'buy' },
          { x: 350, y: 70, type: 'sell' },
        ],
        levels: [
          { y: 60, label: 'Upper Bound' },
          { y: 160, label: 'Lower Bound' },
        ],
      },
    },
  },
  {
    id: 'mean_reversion',
    name: 'Mean Reversion',
    description: 'Captures profits when prices deviate from their moving average and snap back. Statistically-driven entries with built-in risk controls.',
    icon: TrendingDown,
    parameters: [
      { name: 'symbol', label: 'Symbol', type: 'text', default: 'AAPL', required: true },
      { name: 'smaPeriod', label: 'SMA Period', type: 'number', default: 20, required: true },
      { name: 'deviationThreshold', label: 'Deviation Threshold (%)', type: 'number', default: 2, step: 0.1, required: true },
      { name: 'positionSize', label: 'Position Size ($)', type: 'number', default: 1000, required: true },
    ],
    detailData: {
      howItWorks: [
        'Monitors price deviations from a Simple Moving Average (SMA)',
        'Opens long positions when price drops below the deviation threshold',
        'Closes positions when price reverts back toward the mean',
        'Stop-loss and take-profit levels protect against extended trends',
      ],
      bestMarkets: ['Ranging', 'Stocks', 'Low Volatility', 'Indices'],
      tradeFlow: {
        pricePath: 'M 0,100 L 40,85 L 80,55 L 120,70 L 160,100 L 200,135 L 240,155 L 280,130 L 320,100 L 360,65 L 400,85',
        markers: [
          { x: 80, y: 55, type: 'sell' },
          { x: 240, y: 155, type: 'buy' },
          { x: 360, y: 65, type: 'sell' },
        ],
        levels: [
          { y: 100, label: 'SMA Mean' },
        ],
      },
    },
  },
  {
    id: 'momentum',
    name: 'Momentum',
    description: 'Rides strong price trends using RSI and volume confirmation signals. Enters on breakouts and exits when momentum fades.',
    icon: TrendingUp,
    parameters: [
      { name: 'symbol', label: 'Symbol', type: 'text', default: 'TSLA', required: true },
      { name: 'rsiPeriod', label: 'RSI Period', type: 'number', default: 14, required: true },
      { name: 'rsiOverbought', label: 'RSI Overbought', type: 'number', default: 70, required: true },
      { name: 'rsiOversold', label: 'RSI Oversold', type: 'number', default: 30, required: true },
      { name: 'positionSize', label: 'Position Size ($)', type: 'number', default: 1000, required: true },
    ],
    detailData: {
      howItWorks: [
        'Uses RSI indicator to detect overbought and oversold conditions',
        'Enters positions when momentum signals align',
        'Rides strong trends until reversal signals appear',
        'Stop-loss and take-profit levels protect against extended moves',
      ],
      bestMarkets: ['Trending', 'High Volume', 'Stocks', 'Crypto'],
      tradeFlow: {
        pricePath: 'M 0,170 L 50,155 L 100,145 L 140,152 L 190,120 L 240,100 L 280,110 L 330,70 L 380,45',
        markers: [
          { x: 100, y: 145, type: 'buy' },
          { x: 240, y: 100, type: 'buy' },
          { x: 380, y: 45, type: 'sell' },
        ],
        levels: [
          { y: 130, label: 'Breakout Level' },
        ],
      },
    },
  },
  {
    id: 'arbitrage',
    name: 'Arbitrage',
    description: 'Captures profits from price differences across exchanges with simultaneous trades. Executes buy/sell pairs with slippage controls.',
    icon: Repeat,
    parameters: [
      { name: 'symbol', label: 'Symbol', type: 'text', default: 'BTC', required: true },
      { name: 'minSpread', label: 'Min Spread (%)', type: 'number', default: 0.5, step: 0.1, required: true },
      { name: 'positionSize', label: 'Position Size ($)', type: 'number', default: 500, required: true },
      { name: 'maxSlippage', label: 'Max Slippage (%)', type: 'number', default: 0.1, step: 0.01, required: true },
    ],
    detailData: {
      howItWorks: [
        'Monitors the same asset across different exchanges or markets',
        'Detects price discrepancies exceeding the minimum spread threshold',
        'Simultaneously buys low and sells high to capture the spread',
        'Slippage controls ensure profitable execution',
      ],
      bestMarkets: ['Crypto', 'High Volume', 'Multi-Exchange', '24/7 Markets'],
      tradeFlow: {
        pricePath: 'M 0,95 L 60,85 L 120,105 L 180,90 L 240,110 L 300,80 L 360,100 L 400,95',
        pricePath2: 'M 0,115 L 60,130 L 120,115 L 180,135 L 240,120 L 300,140 L 360,115 L 400,112',
        markers: [
          { x: 60, y: 130, type: 'buy' },
          { x: 60, y: 85, type: 'sell' },
          { x: 300, y: 140, type: 'buy' },
          { x: 300, y: 80, type: 'sell' },
        ],
        levels: [],
      },
    },
  },
  {
    id: 'market_making',
    name: 'Market Making',
    description: 'Earns the bid-ask spread by placing simultaneous buy and sell quotes. Supports dynamic Bollinger Band spreads and channel-based bounds.',
    icon: Landmark,
    parameters: [
      { name: 'symbol', label: 'Symbol', type: 'text', default: 'AAPL', required: true },
      { name: 'spread', label: 'Base Spread (%)', type: 'number', default: 0.1, step: 0.01, required: true },
      { name: 'orderSize', label: 'Order Size ($)', type: 'number', default: 100, required: true },
      { name: 'maxPosition', label: 'Max Position ($)', type: 'number', default: 1000, required: true },
      { name: 'inventorySkew', label: 'Inventory Skew', type: 'number', default: 0, step: 0.1, required: false },
    ],
    detailData: {
      howItWorks: [
        'Places simultaneous bid and ask orders around the current price',
        'Dynamic spread mode uses Bollinger Bands to widen spreads near extremes',
        'Bounds enforcement suspends orders outside the trading channel',
        'Inventory skew and stop-loss/take-profit manage position risk',
      ],
      bestMarkets: ['High Volume', 'Stocks', 'Liquid Assets', 'Low Spread'],
      tradeFlow: {
        pricePath: 'M 0,100 L 50,95 L 100,105 L 150,98 L 200,108 L 250,92 L 300,103 L 350,97 L 400,100',
        markers: [
          { x: 100, y: 120, type: 'buy' },
          { x: 150, y: 80, type: 'sell' },
          { x: 250, y: 120, type: 'buy' },
          { x: 300, y: 80, type: 'sell' },
        ],
        levels: [
          { y: 50, label: 'BB Upper' },
          { y: 80, label: 'Ask' },
          { y: 120, label: 'Bid' },
          { y: 150, label: 'BB Lower' },
        ],
      },
    },
  },
];

const inputClasses = 'w-full h-9 px-3 bg-card border border-border rounded-lg text-foreground text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring';

const SIZING_MODES = {
  stocks: [
    { value: 'fixed_shares',     label: 'Fixed Shares',      orderSizeLabel: 'Shares per Level' },
    { value: 'fixed_investment', label: 'Fixed Investment',   orderSizeLabel: 'Investment per Level ($)' },
    { value: 'martingale',       label: 'Martingale/Dynamic', orderSizeLabel: 'Initial Shares' },
  ],
  crypto: [
    { value: 'fixed_quantity',   label: 'Fixed Quantity',     orderSizeLabel: 'Quantity per Level' },
    { value: 'fixed_investment', label: 'Fixed Investment',   orderSizeLabel: 'Investment per Level ($)' },
    { value: 'martingale',       label: 'Martingale/Dynamic', orderSizeLabel: 'Initial Quantity' },
  ],
};

const SIZING_MODE_MAP = {
  fixed_shares: 'fixed_quantity',
  fixed_quantity: 'fixed_shares',
  fixed_investment: 'fixed_investment',
  martingale: 'martingale',
};

const paramTooltips = {
  strategyType: 'Select the algorithmic trading strategy to configure. Each type uses different signals and parameters.',
  gridLevels: 'Number of price levels where buy and sell orders are placed. More levels means more trades but smaller profit per trade.',
  gridSpacing: 'Percentage distance between each grid level relative to the lower bound. Changing this recalculates the upper bound.',
  orderSize: 'Dollar amount allocated to each individual order placed at a grid level.',
  upperBound: 'Highest price in the grid range. Changing this recalculates grid spacing. Cannot be lower than the anchor (lower bound).',
  lowerBound: 'Anchor price for the grid. All levels are calculated upward from this point using the grid spacing.',
  sizingMode: 'How order sizes are calculated at each grid level. Fixed uses a constant size, Martingale increases size after losses.',
  martingaleMultiplier: 'Factor by which order size increases at each subsequent grid level. E.g. 2 means each level doubles the previous size.',
  stopLossMode: 'Whether to apply a stop loss to close all positions if the price moves too far against you.',
  stopLossPercent: 'Percentage below the lower bound at which all positions are liquidated to limit losses.',
  executionMode: 'How the grid is managed at runtime. Auto-Executor continuously monitors positions and fills gaps in the grid. Position-Aware only checks positions at startup and restart.',
  orderType: 'Limit orders are placed at specific prices and wait for the market to reach them. Market orders execute immediately at the current price when the trigger condition is met (e.g., price crosses a grid level).',
  buyDepthMode: 'Controls which buy orders are sent to the broker. "Grid Levels" submits the N closest levels. "Percentage" submits orders within a % of current price.',
  buyDepthN: 'Number of closest buy grid levels to submit to the broker. Remaining levels stay planned and activate as price approaches.',
  buyDepthPercent: 'Maximum distance (%) from current price for a buy order to be submitted. Orders further away stay planned.',
  sellDepthMode: 'Controls which sell orders are sent to the broker. "Grid Levels" submits the N closest levels. "Percentage" submits orders within a % of current price.',
  sellDepthN: 'Number of closest sell grid levels above current price to submit to the broker. Remaining levels stay planned and activate as price approaches.',
  sellDepthPercent: 'Maximum distance (%) above current price for a sell order to be submitted. Orders further away stay planned.',
  takeProfitMode: 'Whether to apply a take profit to close positions when the price moves in your favor by a set percentage.',
  takeProfitPercent: 'Percentage gain at which the position is automatically closed to lock in profits.',
  spreadMode: 'Fixed uses a constant spread percentage. Dynamic adjusts spread using Bollinger Bands — wider near extremes, tighter at the mean.',
  boundsMode: 'None allows orders at any price. Fixed uses manual upper/lower bounds. Dynamic derives bounds from Bollinger Bands.',
  bbPeriod: 'Number of historical bars used to calculate Bollinger Bands. Higher values smooth the channel.',
  bbStdDev: 'Standard deviation multiplier for Bollinger Bands. Higher values widen the channel.',
  gridMode: 'Fixed uses manually set upper/lower bounds. Dynamic derives bounds from Bollinger Bands and adjusts automatically.',
  rebalanceMode: 'Fixed: shifts entire grid up/down preserving spacing when price nears edges. Dynamic: uses Bollinger Bands and ATR to adjust bounds independently (may change spacing).',
  autoRebalance: 'Legacy toggle — use Rebalance Mode instead.',
  minSellReturnPercent: 'Minimum profit percentage above average cost before a sell order is placed. In market mode, sells below this threshold are skipped in favor of higher grid levels.',
  marketSellMode: 'How sell quantity is determined in market order mode. Incremental: each sell matches the buy quantity at its paired grid level. Full Close: sells the entire accumulated position when the trigger fires.',
  rebalanceCooldownMin: 'Minimum minutes between rebalance events. Prevents excessive grid shifts during volatile periods.',
  adaptiveThreshold: 'Scales the deviation threshold based on ATR (volatility). Widens in high volatility, tightens in low volatility.',
  adaptiveRsi: 'Adjusts RSI overbought/oversold bands based on ATR. Widens bands in high volatility, tightens in calm markets.',
  baselineAtrPercent: 'The ATR % that represents "normal" volatility. Current ATR is compared against this to scale thresholds or bands.',
};

function StrategyBuilder() {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEditMode = Boolean(editId);

  const [selectedType, setSelectedType] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    provider: '',
    tradingMode: 'paper',
    assetClass: 'stocks',
    parameters: {},
  });
  const [symbolName, setSymbolName] = useState('');
  const [buffer, setBuffer] = useState(20);
  const [accountBalance, setAccountBalance] = useState(100000);
  const [previewType, setPreviewType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingStrategy, setLoadingStrategy] = useState(false);
  const [brokers, setBrokers] = useState([]);
  const [previewOrders, setPreviewOrders] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [investmentCap, setInvestmentCap] = useState(null);
  const [notificationPrefs, setNotificationPrefs] = useState(null); // null = inherit all from global
  const [notifModalOpen, setNotifModalOpen] = useState(false);
  const [validationModal, setValidationModal] = useState({ open: false, parameterErrors: [], capitalError: null });
  const [enabledOptions, setEnabledOptions] = useState([]);
  const [importConfirm, setImportConfirm] = useState(null); // pending import data
  const { containerRef, isMobile, width: containerWidth } = useContainerMobile();
  const isCompact = !isMobile && containerWidth < 1000;

  // Fetch broker connections once
  useEffect(() => {
    brokerAPI.getAll()
      .then((res) => setBrokers(res.data || []))
      .catch(() => {});
  }, []);

  // Build provider options from active broker connections
  const providerOptions = useMemo(() => {
    const active = brokers.filter((b) => b.status === 'active');
    if (active.length === 0) {
      return [{ value: '', preset: '', label: 'No broker connected', type: 'stocks', environment: 'paper', disabled: true }];
    }
    // Show ALL active connections — no deduplication
    // Each option uses the connection ID as value so paper + live are distinct
    return active.map((b) => ({
      value: b.id,                // broker_connection_id
      preset: b.preset,           // 'alpaca', 'schwab', etc.
      label: `${b.name} (${b.environment === 'live' ? 'Live' : 'Paper'})`,
      type: b.type,
      environment: b.environment,
    }));
  }, [brokers]);

  // Auto-select first provider when options load and no provider is set
  useEffect(() => {
    if (!formData.provider && providerOptions.length > 0 && providerOptions[0].value) {
      const first = providerOptions[0];
      setFormData((prev) => ({
        ...prev,
        provider: first.value,
        providerPreset: first.preset,
        tradingMode: first.environment === 'live' ? 'live' : 'paper',
      }));
    }
  }, [providerOptions, formData.provider]);

  // Derive constraints from the currently selected provider
  const selectedProvider = useMemo(
    () => providerOptions.find((p) => p.value === formData.provider) || providerOptions[0],
    [providerOptions, formData.provider],
  );

  const assetClassLocked = selectedProvider?.type === 'stocks' || selectedProvider?.type === 'crypto';
  // Trading mode is derived from broker environment — auto-synced but not manually editable
  const tradingModeLocked = !!selectedProvider?.environment;
  const InfoTip = ({ id }) => {
    const text = paramTooltips[id];
    const [pos, setPos] = useState(null);
    const iconRef = useRef(null);
    if (!text) return null;
    const show = () => {
      if (!iconRef.current) return;
      const rect = iconRef.current.getBoundingClientRect();
      setPos({ x: rect.left + rect.width / 2, y: rect.top });
    };
    const hide = () => setPos(null);
    return (
      <span className="relative inline-flex ml-1 align-middle">
        <span
          ref={iconRef}
          className="text-muted-foreground hover:text-foreground transition-colors cursor-help"
          onMouseEnter={show}
          onMouseLeave={hide}
        >
          <Info className="h-3.5 w-3.5" />
        </span>
        {pos && ReactDOM.createPortal(
          <div
            className="fixed w-52 px-3 py-2 text-xs text-foreground bg-popover border border-border rounded-lg shadow-lg z-[9999] pointer-events-none"
            style={{ left: pos.x, top: pos.y, transform: 'translate(-50%, -100%) translateY(-8px)' }}
          >
            {text}
            <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-border" />
          </div>,
          document.body,
        )}
      </span>
    );
  };

  // Sync asset class + trading mode when provider changes
  useEffect(() => {
    if (!selectedProvider) return;

    const updates = {};

    // Asset class: lock to provider's type, or default to stocks for 'both'
    if (selectedProvider.type === 'stocks') {
      updates.assetClass = 'stocks';
    } else if (selectedProvider.type === 'crypto') {
      updates.assetClass = 'crypto';
    }
    // For 'both', keep whatever the user has selected (defaults to 'stocks')

    // Trading mode: mirror the broker environment
    updates.tradingMode = selectedProvider.environment || 'paper';

    setFormData((prev) => {
      const merged = { ...prev, ...updates };
      // If asset class actually changed, update the symbol default
      if (updates.assetClass && updates.assetClass !== prev.assetClass) {
        const newSymbol = updates.assetClass === 'crypto' ? 'BTC/USD' : 'AAPL';
        merged.parameters = { ...merged.parameters, symbol: newSymbol };
        // Also update sizing mode for grid strategy
        if (selectedType?.id === 'grid') {
          merged.parameters.sizingMode = updates.assetClass === 'crypto' ? 'fixed_quantity' : 'fixed_shares';
        }
      }
      return merged;
    });
  }, [selectedProvider]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load existing strategy when in edit mode
  useEffect(() => {
    if (!editId || brokers.length === 0) return; // wait for brokers to load
    setLoadingStrategy(true);
    strategiesAPI.getById(editId)
      .then((res) => {
        const s = res.data;
        const type = strategyTypes.find((t) => t.id === s.type);
        if (!type) throw new Error('Unknown strategy type');

        setSelectedType(type);
        const params = typeof s.parameters === 'string' ? JSON.parse(s.parameters) : s.parameters;

        // Resolve broker connection: prefer saved broker_connection_id,
        // then match by preset + environment, then first by preset
        let connId = s.broker_connection_id;
        if (!connId || !brokers.find((b) => b.id === connId)) {
          const envMatch = brokers.find((b) => b.preset === s.provider && b.status === 'active' && b.environment === s.trading_mode);
          const presetMatch = brokers.find((b) => b.preset === s.provider && b.status === 'active');
          connId = (envMatch || presetMatch)?.id || '';
        }
        const matchedBroker = brokers.find((b) => b.id === connId);

        setFormData({
          name: s.name,
          provider: connId,
          providerPreset: s.provider,
          tradingMode: matchedBroker?.environment === 'live' ? 'live' : (s.trading_mode || 'paper'),
          assetClass: params.assetClass || 'stocks',
          parameters: params,
        });
        // Initialize investmentCap from saved strategy
        if (s.investment_cap != null) setInvestmentCap(parseFloat(s.investment_cap));
        if (s.notification_preferences) {
          const np = typeof s.notification_preferences === 'string' ? JSON.parse(s.notification_preferences) : s.notification_preferences;
          setNotificationPrefs(np);
        }
        // Initialize enabledOptions — always show all grid options so new
        // features are discoverable on existing strategies
        if (type.id === 'grid') {
          const opts = ['executionMode', 'stopLoss', 'buyDepth', 'sellDepth'];
          if (params.rebalanceMode && params.rebalanceMode !== 'off') opts.push('rebalanceMode');
          else if (params.autoRebalance) opts.push('rebalanceMode'); // legacy compat
          setEnabledOptions(opts);
        } else if (type.id === 'mean_reversion') {
          const opts = ['executionMode'];
          if ((params.stopLossMode && params.stopLossMode !== 'none') || params.stopLoss != null) opts.push('stopLoss');
          if ((params.takeProfitMode && params.takeProfitMode !== 'none') || params.takeProfit != null) opts.push('takeProfit');
          if (params.adaptiveThreshold) opts.push('adaptiveThreshold');
          setEnabledOptions(opts);
        } else if (type.id === 'momentum') {
          const opts = ['executionMode'];
          if ((params.stopLossMode && params.stopLossMode !== 'none') || params.stopLoss != null) opts.push('stopLoss');
          if ((params.takeProfitMode && params.takeProfitMode !== 'none') || params.takeProfit != null) opts.push('takeProfit');
          if (params.adaptiveRsi) opts.push('adaptiveRsi');
          setEnabledOptions(opts);
        } else if (type.id === 'market_making') {
          const opts = ['executionMode'];
          if (params.spreadMode && params.spreadMode !== 'fixed') opts.push('spreadMode');
          if (params.boundsMode && params.boundsMode !== 'none') opts.push('boundsMode');
          if ((params.stopLossMode && params.stopLossMode !== 'none') || params.stopLoss != null) opts.push('stopLoss');
          if ((params.takeProfitMode && params.takeProfitMode !== 'none') || params.takeProfit != null) opts.push('takeProfit');
          setEnabledOptions(opts);
        } else {
          const optionalNames = type.parameters.filter((p) => !p.required && params[p.name] != null).map((p) => p.name);
          setEnabledOptions(optionalNames);
        }
      })
      .catch((err) => toast.error(`Failed to load strategy: ${err.message}`))
      .finally(() => setLoadingStrategy(false));
  }, [editId, brokers]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch account balance
  useEffect(() => {
    const fetchAccountBalance = async () => {
      try {
        const preset = selectedProvider?.preset || formData.providerPreset || 'alpaca';
        const connectionId = formData.provider; // provider value is the connection ID
        const response = await accountAPI.getInfo(preset, connectionId);
        if (response.success && response.data) {
          const isCrypto = formData.assetClass === 'crypto';
          const bp = isCrypto
            ? (response.data.nonMarginableBuyingPower || response.data.cash)
            : (response.data.buyingPower || response.data.equity);
          setAccountBalance(parseFloat(bp));
        }
      } catch (err) {
        console.error('Error fetching account balance:', err);
      }
    };

    if (selectedType) {
      fetchAccountBalance();
    }
  }, [selectedType, formData.provider, formData.assetClass]);

  // Debounced preview of planned orders (only in edit mode with an existing strategy)
  useEffect(() => {
    if (!isEditMode || !editId || !selectedType) return;

    const timer = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const res = await plannedOrdersAPI.preview(editId, { parameters: formData.parameters });
        setPreviewOrders(res.data || []);
      } catch (err) {
        console.warn('Planned order preview failed:', err.message);
        setPreviewOrders([]);
      } finally {
        setPreviewLoading(false);
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [isEditMode, editId, selectedType, formData.parameters]);

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    const defaultParams = {};
    type.parameters.forEach((param) => {
      defaultParams[param.name] = param.default;
    });
    if (type.id === 'grid') {
      defaultParams.sizingMode = formData.assetClass === 'crypto' ? 'fixed_quantity' : 'fixed_shares';
      defaultParams.martingaleMultiplier = 2;
      defaultParams.stopLossMode = 'none';
      defaultParams.stopLossPercent = 5;
      defaultParams.gridMode = 'fixed';
      defaultParams.rebalanceMode = 'off';
      defaultParams.autoRebalance = false;
      defaultParams.rebalanceCooldownMin = 15;
      defaultParams.bbPeriod = 20;
      defaultParams.bbStdDev = 2;
      defaultParams.orderType = 'limit';
      setEnabledOptions(['executionMode', 'stopLoss', 'buyDepth', 'sellDepth']);
    } else if (type.id === 'mean_reversion') {
      defaultParams.stopLossMode = 'none';
      defaultParams.stopLossPercent = 5;
      defaultParams.takeProfitMode = 'none';
      defaultParams.takeProfitPercent = 3;
      defaultParams.executionMode = 'auto-executor';
      defaultParams.orderType = 'market';
      defaultParams.adaptiveThreshold = false;
      defaultParams.baselineAtrPercent = 1.5;
      setEnabledOptions(['executionMode', 'stopLoss', 'takeProfit']);
    } else if (type.id === 'momentum') {
      defaultParams.stopLossMode = 'none';
      defaultParams.stopLossPercent = 5;
      defaultParams.takeProfitMode = 'none';
      defaultParams.takeProfitPercent = 3;
      defaultParams.executionMode = 'auto-executor';
      defaultParams.orderType = 'market';
      defaultParams.adaptiveRsi = false;
      defaultParams.baselineAtrPercent = 1.5;
      setEnabledOptions(['executionMode', 'stopLoss', 'takeProfit']);
    } else if (type.id === 'market_making') {
      defaultParams.spreadMode = 'fixed';
      defaultParams.boundsMode = 'none';
      defaultParams.bbPeriod = 20;
      defaultParams.bbStdDev = 2;
      defaultParams.stopLossMode = 'none';
      defaultParams.stopLossPercent = 5;
      defaultParams.takeProfitMode = 'none';
      defaultParams.takeProfitPercent = 3;
      defaultParams.executionMode = 'auto-executor';
      defaultParams.orderType = 'limit';
      setEnabledOptions(['executionMode', 'spreadMode', 'boundsMode', 'stopLoss', 'takeProfit']);
    } else {
      // Enable all optional params by default for other types
      const optionalNames = type.parameters.filter((p) => !p.required).map((p) => p.name);
      setEnabledOptions(optionalNames);
    }
    setFormData({
      ...formData,
      name: `${type.name} Strategy`,
      parameters: defaultParams,
    });
  };

  const handleInputChange = (field, value) => {
    if (field === 'assetClass') {
      const newParams = { ...formData.parameters };
      // Default symbol when switching asset class
      if (value === 'crypto' && formData.assetClass !== 'crypto') {
        newParams.symbol = 'BTC/USD';
      } else if (value === 'stocks' && formData.assetClass !== 'stocks') {
        newParams.symbol = 'AAPL';
      }
      // Map sizing mode for grid strategies
      if (selectedType?.id === 'grid') {
        const currentMode = formData.parameters.sizingMode;
        newParams.sizingMode = currentMode
          ? (SIZING_MODE_MAP[currentMode] || currentMode)
          : (value === 'crypto' ? 'fixed_quantity' : 'fixed_shares');
      }
      setFormData({ ...formData, [field]: value, parameters: newParams });
      return;
    }
    if (field === 'provider') {
      // When provider changes, the useEffect on selectedProvider handles sync
      setFormData({ ...formData, [field]: value });
      return;
    }
    setFormData({ ...formData, [field]: value });
  };

  const handleParameterChange = (paramName, value) => {
    const updated = { ...formData.parameters, [paramName]: value };

    // Grid correlation: lowerBound is the anchor.
    // Changing gridSpacing or gridLevels → recalculate upperBound.
    // Changing upperBound → recalculate gridSpacing.
    // Changing lowerBound → recalculate upperBound (preserve spacing).
    if (selectedType?.id === 'grid') {
      const spacing = paramName === 'gridSpacing' ? value : (updated.gridSpacing || 0);
      const levels = paramName === 'gridLevels' ? value : (updated.gridLevels || 2);
      const lower = paramName === 'lowerBound' ? value : (updated.lowerBound || 0);

      if (paramName === 'gridSpacing' || paramName === 'gridLevels' || paramName === 'lowerBound') {
        if (spacing > 0 && levels >= 2 && lower > 0) {
          updated.upperBound = parseFloat((lower * (1 + spacing / 100 * (levels - 1))).toFixed(2));
        }
      } else if (paramName === 'upperBound') {
        const upper = value;
        const curLower = updated.lowerBound || 0;
        const curLevels = updated.gridLevels || 2;
        // Enforce: upper cannot go below lower
        if (upper <= curLower) {
          updated.upperBound = parseFloat((curLower + 0.01).toFixed(2));
        }
        if (curLower > 0 && curLevels >= 2 && updated.upperBound > curLower) {
          updated.gridSpacing = parseFloat(((updated.upperBound / curLower - 1) / (curLevels - 1) * 100).toFixed(4));
        }
      }

      // Final guard: upper must never be below lower
      if (updated.upperBound != null && updated.lowerBound != null && updated.upperBound < updated.lowerBound) {
        updated.upperBound = updated.lowerBound;
      }
    }

    setFormData({ ...formData, parameters: updated });
  };

  const handleBoundsChange = useCallback(({ upper, lower }) => {
    setFormData((prev) => {
      const params = { ...prev.parameters, upperBound: upper, lowerBound: lower };
      // Enforce upper >= lower
      if (params.upperBound < params.lowerBound) {
        params.upperBound = params.lowerBound;
      }
      // Recalculate gridSpacing from new bounds
      const levels = params.gridLevels || 2;
      if (params.lowerBound > 0 && levels >= 2 && params.upperBound > params.lowerBound) {
        params.gridSpacing = parseFloat(((params.upperBound / params.lowerBound - 1) / (levels - 1) * 100).toFixed(4));
      }
      return { ...prev, parameters: params };
    });
  }, []);

  const handleImportRequest = (parsed) => {
    // Show confirm before overwriting form data
    setImportConfirm(parsed);
  };

  const handleImportConfirm = () => {
    const parsed = importConfirm;
    setImportConfirm(null);
    const type = strategyTypes.find((t) => t.id === parsed.type);
    if (!type) {
      toast.error(`Unknown strategy type: ${parsed.type}`);
      return;
    }
    setSelectedType(type);
    const params = parsed.parameters || {};
    setFormData({
      name: parsed.name || '',
      provider: parsed.provider || providerOptions[0]?.value || '',
      tradingMode: parsed.trading_mode || 'paper',
      assetClass: params.assetClass || 'stocks',
      parameters: params,
    });
    // Initialize enabledOptions from imported params
    if (type.id === 'grid') {
      const opts = [];
      if (params.executionMode) opts.push('executionMode');
      if (params.stopLossMode && params.stopLossMode !== 'none') opts.push('stopLoss');
      if (params.buyDepthMode || params.buyDepthN != null || params.buyDepthPercent != null) opts.push('buyDepth');
      if (params.sellDepthMode || params.sellDepthN != null || params.sellDepthPercent != null) opts.push('sellDepth');
      if (params.rebalanceMode && params.rebalanceMode !== 'off') opts.push('rebalanceMode');
      else if (params.autoRebalance) opts.push('rebalanceMode'); // legacy compat
      setEnabledOptions(opts);
    } else if (type.id === 'mean_reversion') {
      const opts = ['executionMode'];
      if ((params.stopLossMode && params.stopLossMode !== 'none') || params.stopLoss != null) opts.push('stopLoss');
      if ((params.takeProfitMode && params.takeProfitMode !== 'none') || params.takeProfit != null) opts.push('takeProfit');
      if (params.adaptiveThreshold) opts.push('adaptiveThreshold');
      setEnabledOptions(opts);
    } else if (type.id === 'momentum') {
      const opts = ['executionMode'];
      if ((params.stopLossMode && params.stopLossMode !== 'none') || params.stopLoss != null) opts.push('stopLoss');
      if ((params.takeProfitMode && params.takeProfitMode !== 'none') || params.takeProfit != null) opts.push('takeProfit');
      if (params.adaptiveRsi) opts.push('adaptiveRsi');
      setEnabledOptions(opts);
    } else if (type.id === 'market_making') {
      const opts = ['executionMode'];
      if (params.spreadMode && params.spreadMode !== 'fixed') opts.push('spreadMode');
      if (params.boundsMode && params.boundsMode !== 'none') opts.push('boundsMode');
      if ((params.stopLossMode && params.stopLossMode !== 'none') || params.stopLoss != null) opts.push('stopLoss');
      if ((params.takeProfitMode && params.takeProfitMode !== 'none') || params.takeProfit != null) opts.push('takeProfit');
      setEnabledOptions(opts);
    } else {
      const optionalNames = type.parameters.filter((p) => !p.required && params[p.name] != null).map((p) => p.name);
      setEnabledOptions(optionalNames);
    }
    toast.success('Strategy imported');
  };

  // Validate strategy parameters before submission
  const validateStrategy = () => {
    const errors = [];
    const params = formData.parameters;

    if (!formData.name?.trim()) {
      errors.push('Strategy name is required');
    }
    if (!formData.provider) {
      errors.push('Broker connection is required');
    }

    // Check required parameters defined in the strategy type
    if (selectedType) {
      for (const p of selectedType.parameters) {
        if (p.required && (params[p.name] == null || params[p.name] === '')) {
          errors.push(`${p.label} is required`);
        }
      }
    }

    // Grid-specific validation
    if (selectedType?.id === 'grid') {
      const { gridMode, lowerBound, upperBound, gridLevels, gridSpacing } = params;
      if (gridMode === 'fixed' || !gridMode) {
        if (lowerBound && upperBound && Number(lowerBound) >= Number(upperBound)) {
          errors.push('Lower Bound must be less than Upper Bound');
        }
        if (!lowerBound && !upperBound && (!gridSpacing || gridSpacing <= 0)) {
          errors.push('Fixed grid mode requires Upper/Lower Bounds or Grid Spacing');
        }
      }
      if (gridLevels != null && (gridLevels < 2 || gridLevels > 100)) {
        errors.push('Grid Levels must be between 2 and 100');
      }
    }

    // Mean Reversion / Momentum numeric checks
    if (selectedType?.id === 'mean_reversion' && params.smaPeriod != null && params.smaPeriod < 2) {
      errors.push('SMA Period must be at least 2');
    }
    if (selectedType?.id === 'momentum') {
      if (params.rsiOverbought != null && params.rsiOversold != null && Number(params.rsiOversold) >= Number(params.rsiOverbought)) {
        errors.push('RSI Oversold must be less than RSI Overbought');
      }
    }

    // Capital sufficiency check
    let capitalError = null;
    if (selectedType && accountBalance > 0) {
      const requiredCapital = calculateRequiredCapital(selectedType.id, params);
      const bufferAmount = (requiredCapital * buffer) / 100;
      const totalRequired = requiredCapital + bufferAmount;
      if (totalRequired > accountBalance) {
        capitalError = { required: totalRequired, available: accountBalance };
      }
    }

    return { errors, capitalError };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Pre-submit validation
    const { errors, capitalError } = validateStrategy();
    if (errors.length > 0 || capitalError) {
      setValidationModal({ open: true, parameterErrors: errors, capitalError });
      return;
    }

    setLoading(true);

    try {
      const params = { ...formData.parameters, assetClass: formData.assetClass };

      if (isEditMode) {
        const selectedOpt = providerOptions.find((p) => p.value === formData.provider);
        const updatePayload = {
          name: formData.name,
          trading_mode: selectedOpt?.environment === 'live' ? 'live' : formData.tradingMode,
          parameters: params,
          broker_connection_id: formData.provider || null, // connection ID
        };
        if (investmentCap != null) updatePayload.investment_cap = investmentCap;
        if (notificationPrefs) updatePayload.notification_preferences = notificationPrefs;
        await strategiesAPI.update(editId, updatePayload);
        toast.success('Strategy updated');
      } else {
        // provider = preset name (e.g. 'alpaca'), brokerConnectionId = specific connection
        const selectedOpt = providerOptions.find((p) => p.value === formData.provider);
        await strategiesAPI.create({
          name: formData.name,
          type: selectedType.id,
          provider: selectedOpt?.preset || formData.providerPreset || formData.provider,
          tradingMode: formData.tradingMode,
          parameters: params,
          investmentCap,
          brokerConnectionId: formData.provider, // connection ID
        });
        toast.success('Strategy created');
      }
      navigate('/strategies/manager');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Stable ref: only pass handleBoundsChange when the strategy type has bounds
  const hasBoundsParam = useMemo(
    () => selectedType?.parameters.some((p) => p.name === 'upperBound'),
    [selectedType],
  );
  const boundsChangeHandler = hasBoundsParam ? handleBoundsChange : undefined;

  // Derive dynamic orderSize label based on sizing mode
  const orderSizeLabelOverride = useMemo(() => {
    if (selectedType?.id !== 'grid') return null;
    const modes = SIZING_MODES[formData.assetClass] || SIZING_MODES.stocks;
    const currentMode = formData.parameters.sizingMode;
    const match = modes.find((m) => m.value === currentMode);
    return match?.orderSizeLabel || null;
  }, [selectedType, formData.assetClass, formData.parameters.sizingMode]);

  // Split strategy parameters into 3 columns for the bottom section
  const paramColumns = useMemo(() => {
    if (!selectedType) return [[], [], []];
    const params = selectedType.parameters;
    const third = Math.ceil(params.length / 3);
    return [
      params.slice(0, third),
      params.slice(third, third * 2),
      params.slice(third * 2),
    ];
  }, [selectedType]);

  const renderParamInput = (param) => {
    const displayLabel = (param.name === 'orderSize' && orderSizeLabelOverride) ? orderSizeLabelOverride : param.label;
    return (
    <div key={param.name}>
      <label className="block text-xs font-medium text-muted-foreground mb-1">
        {param.name === 'lowerBound' && selectedType?.id === 'grid' && (
          <Anchor className="inline-block w-3 h-3 mr-1 -mt-0.5 text-primary" />
        )}
        {displayLabel} {param.required && '*'}
        {paramTooltips[param.name] && <InfoTip id={param.name} />}
      </label>
      {param.name === 'symbol' ? (
        <SymbolSearch
          value={formData.parameters[param.name] || ''}
          assetClass={formData.assetClass}
          onChange={(value, name) => {
            handleParameterChange(param.name, value);
            if (name) setSymbolName(name);
          }}
        />
      ) : param.type === 'select' && param.options ? (
        <select
          value={formData.parameters[param.name] ?? param.default ?? ''}
          onChange={(e) => handleParameterChange(param.name, e.target.value)}
          className={inputClasses}
          required={param.required}
        >
          {param.options.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      ) : (
        <input
          type={param.type}
          value={formData.parameters[param.name] ?? ''}
          onChange={(e) =>
            handleParameterChange(
              param.name,
              param.type === 'number' ? parseFloat(e.target.value) : e.target.value
            )
          }
          step={param.step}
          className={inputClasses}
          required={param.required}
        />
      )}
    </div>
  );
  };

  if (loadingStrategy) {
    return (
      <PageTransition className="p-6">
        <div className="flex items-center justify-center" style={{ minHeight: '400px' }}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      </PageTransition>
    );
  }

  // Strategy type selection screen
  if (!selectedType) {
    return (
      <PageTransition className="p-6">
        <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {strategyTypes.map((type) => {
            const Icon = type.icon;
            const isComingSoon = type.id === 'arbitrage';
            return (
              <AnimatedItem key={type.id}>
                <Card
                  className={isComingSoon
                    ? 'opacity-50 cursor-not-allowed transition-colors'
                    : 'hover:border-primary transition-colors cursor-pointer'}
                  onClick={isComingSoon ? undefined : () => setPreviewType(type)}
                >
                  <div className="text-center py-4 relative">
                    {isComingSoon && (
                      <span className="absolute top-2 right-2 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded-full">
                        Coming Soon
                      </span>
                    )}
                    <div className="flex justify-center mb-4 text-primary">
                      <Icon className="h-10 w-10" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground mb-1">{type.name}</h3>
                    <p className="text-sm text-muted-foreground mb-3">{type.description}</p>
                    {isComingSoon ? (
                      <p className="text-xs italic text-muted-foreground/60">Under Development</p>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTypeSelect(type);
                        }}
                      >
                        Select →
                      </Button>
                    )}
                  </div>
                </Card>
              </AnimatedItem>
            );
          })}
        </StaggerContainer>

        <StrategyDetailModal
          strategy={previewType}
          open={!!previewType}
          onClose={() => setPreviewType(null)}
          onSelect={(type) => {
            if (type.id === 'arbitrage') return;
            setPreviewType(null);
            handleTypeSelect(type);
          }}
        />
      </PageTransition>
    );
  }

  // Build props for MobileStrategyBuilder
  const builderProps = {
    selectedType,
    formData,
    symbolName,
    accountBalance,
    buffer,
    setBuffer,
    isEditMode,
    loading,
    providerOptions,
    tradingModeLocked,
    assetClassLocked,
    selectedProvider,
    paramColumns,
    enabledOptions,
    setEnabledOptions,
    previewOrders,
    previewLoading,
    boundsChangeHandler: boundsChangeHandler,
    handleInputChange,
    handleParameterChange,
    handleTypeSelect,
    handleSubmit,
    handleImportRequest,
    onNotifications: () => setNotifModalOpen(true),
    renderParamInput,
    InfoTip,
    navigate,
    strategyTypes,
  };

  // Strategy builder form — 8-column grid layout
  return (
    <PageTransition className="h-full flex flex-col p-3 sm:p-6 gap-5">
      <div ref={containerRef} className="flex flex-col gap-5 flex-1 min-h-0">
      {isMobile ? (
        <MobileStrategyBuilder {...builderProps} />
      ) : (
      <form onSubmit={handleSubmit} className="flex flex-col gap-5 flex-1 min-h-0 overflow-hidden">
        {/* ── Top section: Chart + Capital Requirements ── */}
        {isCompact ? (
          /* Compact layout (< 1000px): stacked — account info above chart */
          <div className="flex flex-col gap-3 shrink-0">
            <CollapsibleAccountInfo
              strategyType={selectedType.id}
              parameters={formData.parameters}
              accountBalance={accountBalance}
              buffer={buffer}
              onBufferChange={setBuffer}
            />
            <div className="min-h-[300px] h-[45vh]">
              <CandlestickChart
                symbol={formData.parameters.symbol}
                symbolName={symbolName}
                upperBound={formData.parameters.upperBound}
                lowerBound={formData.parameters.lowerBound}
                gridLevels={
                  selectedType.id === 'grid' ? formData.parameters.gridLevels : undefined
                }
                timeframe="1M"
                onBoundsChange={boundsChangeHandler}
              />
            </div>
          </div>
        ) : (
          /* Wide layout (>= 1000px): 8-col grid — chart 6 cols + sidebar 2 cols */
          <div className="grid grid-cols-8 gap-5 shrink-0" style={{ height: 'clamp(300px, 45vh, 500px)' }}>
            <div className="col-span-6 relative">
              <div className="absolute inset-0">
                <CandlestickChart
                  symbol={formData.parameters.symbol}
                  symbolName={symbolName}
                  upperBound={formData.parameters.upperBound}
                  lowerBound={formData.parameters.lowerBound}
                  gridLevels={
                    selectedType.id === 'grid' ? formData.parameters.gridLevels : undefined
                  }
                  timeframe="1M"
                  onBoundsChange={boundsChangeHandler}
                />
              </div>
            </div>
            <div className="col-span-2 flex flex-col gap-4 overflow-y-auto">
              <CapitalRequirements
                strategyType={selectedType.id}
                parameters={formData.parameters}
                accountBalance={accountBalance}
                buffer={buffer}
                onBufferChange={setBuffer}
                onInvestmentCapChange={setInvestmentCap}
              />
            </div>
          </div>
        )}

        {/* ── Bottom section: parameter cards with flex-wrap ── */}
        <div className="flex gap-5 flex-1 min-h-0 overflow-hidden">
          {/* Column 1: Configuration (qualifying questions) */}
          <div className="flex-1 min-w-[200px] flex min-h-0">
          <Card className="flex-1" scrollable title="Configuration" action={selectedType && (
            <StrategyToolsDropdown
              mode="builder"
              formData={formData}
              selectedType={selectedType}
              onImport={handleImportRequest}
              onNotifications={() => setNotifModalOpen(true)}
            />
          )}>
            <div className="flex flex-col h-full">
            <div className="space-y-3 flex-1">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                  Strategy Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={inputClasses}
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                  Strategy Type * <InfoTip id="strategyType" />
                </label>
                <select
                  value={selectedType.id}
                  onChange={(e) => {
                    const type = strategyTypes.find((t) => t.id === e.target.value);
                    if (type) handleTypeSelect(type);
                  }}
                  className={inputClasses}
                  disabled={isEditMode}
                  required
                >
                  {strategyTypes.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                  Provider *
                </label>
                <select
                  value={formData.provider}
                  onChange={(e) => {
                    const opt = providerOptions.find((p) => p.value === e.target.value);
                    setFormData((prev) => ({
                      ...prev,
                      provider: e.target.value,
                      providerPreset: opt?.preset || '',
                      // Auto-sync trading mode from broker environment
                      tradingMode: opt?.environment === 'live' ? 'live' : 'paper',
                    }));
                  }}
                  className={inputClasses}
                  required
                >
                  {providerOptions.map((p) => {
                    const typeLabel = p.type === 'both' ? 'Stocks + Crypto'
                      : p.type === 'crypto' ? 'Crypto'
                      : 'Stocks';
                    return (
                      <option key={p.value} value={p.value}>
                        {p.label} ({typeLabel})
                      </option>
                    );
                  })}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                  Trading Mode *
                </label>
                <select
                  value={formData.tradingMode}
                  onChange={(e) => handleInputChange('tradingMode', e.target.value)}
                  className={inputClasses}
                  disabled={tradingModeLocked}
                  required
                >
                  <option value="paper">Paper Trading (Simulated)</option>
                  <option value="live">Live Trading (Real Money)</option>
                </select>
                {tradingModeLocked && (
                  <p className="text-[10px] text-muted-foreground/60 mt-1">
                    Set by broker environment
                  </p>
                )}
              </div>

            </div>

              {/* Save / Cancel */}
              <div className="pt-3 mt-3 border-t border-border flex items-center gap-3">
                <Button type="submit" loading={loading} className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white">
                  {loading
                    ? (isEditMode ? 'Saving...' : 'Creating...')
                    : (isEditMode ? 'Save Changes' : 'Create Strategy')}
                </Button>
                <Button type="button" variant="ghost" className="text-red-400/70 hover:text-red-400 hover:bg-red-400/10" onClick={() => navigate('/strategies/manager')}>
                  Cancel
                </Button>
              </div>
            </div>
          </Card>
          </div>

          {/* Columns 2-4: Strategy Parameters */}
          {paramColumns.map((group, colIdx) => (
            <div key={colIdx} className="flex-1 min-w-[200px] flex min-h-0">
            <Card
              className="flex-1"
              scrollable
              title={
                colIdx === 0
                  ? 'Symbol & Sizing'
                  : colIdx === 1
                  ? 'Thresholds'
                  : 'Limits'
              }
            >
              <div className="space-y-3">
                {colIdx === 0 && (
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                      Asset Class *
                    </label>
                    <select
                      value={formData.assetClass}
                      onChange={(e) => handleInputChange('assetClass', e.target.value)}
                      className={inputClasses}
                      disabled={assetClassLocked}
                      required
                    >
                      <option value="stocks">Stocks</option>
                      <option value="crypto">Crypto</option>
                    </select>
                    {assetClassLocked && (
                      <p className="text-[10px] text-muted-foreground/60 mt-1">
                        Set by provider ({selectedProvider?.type})
                      </p>
                    )}
                  </div>
                )}
                {colIdx === 0 && selectedType?.id === 'grid' && (
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                      Sizing Mode * <InfoTip id="sizingMode" />
                    </label>
                    <select
                      value={formData.parameters.sizingMode || 'fixed_shares'}
                      onChange={(e) => handleParameterChange('sizingMode', e.target.value)}
                      className={inputClasses}
                      required
                    >
                      {(SIZING_MODES[formData.assetClass] || SIZING_MODES.stocks).map((m) => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                  </div>
                )}
                {colIdx === 0 && (
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                      Order Execution
                    </label>
                    <select
                      value={formData.parameters.orderExecution || 'adaptive'}
                      onChange={(e) => handleParameterChange('orderExecution', e.target.value)}
                      className={inputClasses}
                    >
                      <option value="adaptive">Adaptive (Orderbook-aware)</option>
                      <option value="strict">Standard (Strict limit orders)</option>
                    </select>
                    <p className="text-[10px] text-muted-foreground/60 mt-1">
                      Adaptive distributes orders across orderbook liquidity for faster fills
                    </p>
                  </div>
                )}
                {group.map((param) => {
                  // For grid: skip upperBound/lowerBound in Limits col — they're in Thresholds
                  if (colIdx === 2 && selectedType?.id === 'grid' && (param.name === 'upperBound' || param.name === 'lowerBound')) return null;
                  // For grid: skip gridLevels in Symbol & Sizing col — it's in Thresholds
                  if (colIdx === 0 && selectedType?.id === 'grid' && param.name === 'gridLevels') return null;
                  // For non-grid: skip optional params here — they render below with MultiSelectDropdown
                  if (colIdx === 2 && !param.required && selectedType?.id !== 'grid') return null;
                  return renderParamInput(param);
                })}
                {colIdx === 1 && selectedType?.id === 'grid' && formData.parameters.sizingMode === 'martingale' && (
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                      Martingale Multiplier * <InfoTip id="martingaleMultiplier" />
                    </label>
                    <input
                      type="number"
                      value={formData.parameters.martingaleMultiplier ?? 2}
                      onChange={(e) => handleParameterChange('martingaleMultiplier', parseFloat(e.target.value))}
                      min={1.1}
                      step={0.1}
                      className={inputClasses}
                      required
                    />
                  </div>
                )}
                {/* Grid: render gridLevels + upper/lower bounds in Thresholds column */}
                {colIdx === 1 && selectedType?.id === 'grid' && (
                  <>
                    {renderParamInput(selectedType.parameters.find((p) => p.name === 'gridLevels'))}
                    {renderParamInput(selectedType.parameters.find((p) => p.name === 'upperBound'))}
                    {renderParamInput(selectedType.parameters.find((p) => p.name === 'lowerBound'))}
                  </>
                )}
                {/* MeanReversion / Momentum / MarketMaking Limits column: stop loss, take profit, execution mode */}
                {colIdx === 2 && (selectedType?.id === 'mean_reversion' || selectedType?.id === 'momentum' || selectedType?.id === 'market_making') && (
                  <>
                    <MultiSelectDropdown
                      label="Options"
                      icon={SlidersHorizontal}
                      options={[
                        { value: 'executionMode', label: 'Execution Mode' },
                        ...(selectedType?.id === 'market_making' ? [
                          { value: 'spreadMode', label: 'Dynamic Spread (BB)' },
                          { value: 'boundsMode', label: 'Price Bounds' },
                        ] : []),
                        ...(selectedType?.id === 'mean_reversion' ? [
                          { value: 'adaptiveThreshold', label: 'Adaptive Threshold (ATR)' },
                        ] : []),
                        ...(selectedType?.id === 'momentum' ? [
                          { value: 'adaptiveRsi', label: 'Adaptive RSI (ATR)' },
                        ] : []),
                        { value: 'stopLoss', label: 'Stop Loss' },
                        { value: 'takeProfit', label: 'Take Profit' },
                      ]}
                      selected={enabledOptions}
                      onChange={setEnabledOptions}
                    />
                    {enabledOptions.includes('stopLoss') && (
                      <div className="flex items-end gap-2">
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                            Stop Loss Mode <InfoTip id="stopLossMode" />
                          </label>
                          <select
                            value={formData.parameters.stopLossMode || 'none'}
                            onChange={(e) => handleParameterChange('stopLossMode', e.target.value)}
                            className={inputClasses}
                          >
                            <option value="none">None (Open-ended)</option>
                            <option value="percentage">Percentage</option>
                          </select>
                        </div>
                        {formData.parameters.stopLossMode === 'percentage' && (
                          <div className="w-[20%]">
                            <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                              % <InfoTip id="stopLossPercent" />
                            </label>
                            <input
                              type="number"
                              value={formData.parameters.stopLossPercent ?? 5}
                              onChange={(e) => handleParameterChange('stopLossPercent', parseFloat(e.target.value))}
                              min={0.1}
                              step={0.1}
                              className={inputClasses}
                              required
                            />
                          </div>
                        )}
                      </div>
                    )}
                    {enabledOptions.includes('takeProfit') && (
                      <div className="flex items-end gap-2">
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                            Take Profit Mode <InfoTip id="takeProfitMode" />
                          </label>
                          <select
                            value={formData.parameters.takeProfitMode || 'none'}
                            onChange={(e) => handleParameterChange('takeProfitMode', e.target.value)}
                            className={inputClasses}
                          >
                            <option value="none">None (Open-ended)</option>
                            <option value="percentage">Percentage</option>
                          </select>
                        </div>
                        {formData.parameters.takeProfitMode === 'percentage' && (
                          <div className="w-[20%]">
                            <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                              % <InfoTip id="takeProfitPercent" />
                            </label>
                            <input
                              type="number"
                              value={formData.parameters.takeProfitPercent ?? 3}
                              onChange={(e) => handleParameterChange('takeProfitPercent', parseFloat(e.target.value))}
                              min={0.1}
                              step={0.1}
                              className={inputClasses}
                              required
                            />
                          </div>
                        )}
                      </div>
                    )}
                    {enabledOptions.includes('executionMode') && (
                      <div>
                        <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                          Execution Mode * <InfoTip id="executionMode" />
                        </label>
                        <select
                          value={formData.parameters.executionMode || 'auto-executor'}
                          onChange={(e) => handleParameterChange('executionMode', e.target.value)}
                          className={inputClasses}
                          required
                        >
                          <option value="auto-executor">Auto-Executor (Continuous)</option>
                        </select>
                      </div>
                    )}
                    {enabledOptions.includes('executionMode') && (
                      <div>
                        <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                          Order Type * <InfoTip id="orderType" />
                        </label>
                        <select
                          value={formData.parameters.orderType || 'limit'}
                          onChange={(e) => handleParameterChange('orderType', e.target.value)}
                          className={inputClasses}
                          required
                        >
                          <option value="limit">Limit Orders</option>
                          <option value="market">Market Orders</option>
                        </select>
                      </div>
                    )}
                    {/* Market Making: Spread Mode */}
                    {selectedType?.id === 'market_making' && enabledOptions.includes('spreadMode') && (
                      <>
                        <div>
                          <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                            Spread Mode <InfoTip id="spreadMode" />
                          </label>
                          <select
                            value={formData.parameters.spreadMode || 'fixed'}
                            onChange={(e) => handleParameterChange('spreadMode', e.target.value)}
                            className={inputClasses}
                          >
                            <option value="fixed">Fixed</option>
                            <option value="dynamic">Dynamic (Bollinger Bands)</option>
                          </select>
                        </div>
                        {formData.parameters.spreadMode === 'dynamic' && (
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                                BB Period <InfoTip id="bbPeriod" />
                              </label>
                              <input
                                type="number"
                                value={formData.parameters.bbPeriod ?? 20}
                                onChange={(e) => handleParameterChange('bbPeriod', parseInt(e.target.value, 10))}
                                min={2}
                                max={200}
                                className={inputClasses}
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                                BB Std Dev <InfoTip id="bbStdDev" />
                              </label>
                              <input
                                type="number"
                                value={formData.parameters.bbStdDev ?? 2}
                                onChange={(e) => handleParameterChange('bbStdDev', parseFloat(e.target.value))}
                                min={0.5}
                                max={5}
                                step={0.1}
                                className={inputClasses}
                              />
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    {/* Market Making: Bounds Mode */}
                    {selectedType?.id === 'market_making' && enabledOptions.includes('boundsMode') && (
                      <>
                        <div>
                          <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                            Bounds Mode <InfoTip id="boundsMode" />
                          </label>
                          <select
                            value={formData.parameters.boundsMode || 'none'}
                            onChange={(e) => handleParameterChange('boundsMode', e.target.value)}
                            className={inputClasses}
                          >
                            <option value="none">None</option>
                            <option value="fixed">Fixed (Manual)</option>
                            <option value="dynamic">Dynamic (Bollinger Bands)</option>
                          </select>
                        </div>
                        {formData.parameters.boundsMode === 'fixed' && (
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                                Upper Bound ($)
                              </label>
                              <input
                                type="number"
                                value={formData.parameters.upperBound ?? ''}
                                onChange={(e) => handleParameterChange('upperBound', parseFloat(e.target.value))}
                                step={0.01}
                                className={inputClasses}
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                                Lower Bound ($)
                              </label>
                              <input
                                type="number"
                                value={formData.parameters.lowerBound ?? ''}
                                onChange={(e) => handleParameterChange('lowerBound', parseFloat(e.target.value))}
                                step={0.01}
                                className={inputClasses}
                              />
                            </div>
                          </div>
                        )}
                        {formData.parameters.boundsMode === 'dynamic' && formData.parameters.spreadMode !== 'dynamic' && (
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                                BB Period <InfoTip id="bbPeriod" />
                              </label>
                              <input
                                type="number"
                                value={formData.parameters.bbPeriod ?? 20}
                                onChange={(e) => handleParameterChange('bbPeriod', parseInt(e.target.value, 10))}
                                min={2}
                                max={200}
                                className={inputClasses}
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                                BB Std Dev <InfoTip id="bbStdDev" />
                              </label>
                              <input
                                type="number"
                                value={formData.parameters.bbStdDev ?? 2}
                                onChange={(e) => handleParameterChange('bbStdDev', parseFloat(e.target.value))}
                                min={0.5}
                                max={5}
                                step={0.1}
                                className={inputClasses}
                              />
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    {/* Mean Reversion: Adaptive Threshold */}
                    {selectedType?.id === 'mean_reversion' && enabledOptions.includes('adaptiveThreshold') && (
                      <>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="adaptiveThreshold"
                            checked={formData.parameters.adaptiveThreshold || false}
                            onChange={(e) => handleParameterChange('adaptiveThreshold', e.target.checked)}
                            className="h-4 w-4 rounded border-border bg-card text-primary focus:ring-ring"
                          />
                          <label htmlFor="adaptiveThreshold" className="text-xs font-medium text-muted-foreground">
                            Adaptive Threshold <InfoTip id="adaptiveThreshold" />
                          </label>
                        </div>
                        {formData.parameters.adaptiveThreshold && (
                          <div>
                            <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                              Baseline ATR (%) <InfoTip id="baselineAtrPercent" />
                            </label>
                            <input
                              type="number"
                              value={formData.parameters.baselineAtrPercent ?? 1.5}
                              onChange={(e) => handleParameterChange('baselineAtrPercent', parseFloat(e.target.value))}
                              min={0.1}
                              max={10}
                              step={0.1}
                              className={inputClasses}
                            />
                          </div>
                        )}
                      </>
                    )}
                    {/* Momentum: Adaptive RSI */}
                    {selectedType?.id === 'momentum' && enabledOptions.includes('adaptiveRsi') && (
                      <>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="adaptiveRsi"
                            checked={formData.parameters.adaptiveRsi || false}
                            onChange={(e) => handleParameterChange('adaptiveRsi', e.target.checked)}
                            className="h-4 w-4 rounded border-border bg-card text-primary focus:ring-ring"
                          />
                          <label htmlFor="adaptiveRsi" className="text-xs font-medium text-muted-foreground">
                            Adaptive RSI <InfoTip id="adaptiveRsi" />
                          </label>
                        </div>
                        {formData.parameters.adaptiveRsi && (
                          <div>
                            <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                              Baseline ATR (%) <InfoTip id="baselineAtrPercent" />
                            </label>
                            <input
                              type="number"
                              value={formData.parameters.baselineAtrPercent ?? 1.5}
                              onChange={(e) => handleParameterChange('baselineAtrPercent', parseFloat(e.target.value))}
                              min={0.1}
                              max={10}
                              step={0.1}
                              className={inputClasses}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
                {/* Other non-grid types: optional params gated by MultiSelectDropdown */}
                {colIdx === 2 && selectedType?.id !== 'grid' && selectedType?.id !== 'mean_reversion' && selectedType?.id !== 'momentum' && selectedType?.id !== 'market_making' && (() => {
                  const optionalParams = selectedType?.parameters.filter((p) => !p.required) || [];
                  if (optionalParams.length === 0) return null;
                  return (
                    <>
                      <MultiSelectDropdown
                        label="Options"
                        icon={SlidersHorizontal}
                        options={optionalParams.map((p) => ({ value: p.name, label: p.label }))}
                        selected={enabledOptions}
                        onChange={setEnabledOptions}
                      />
                      {optionalParams.filter((p) => enabledOptions.includes(p.name)).map((param) => renderParamInput(param))}
                    </>
                  );
                })()}
                {/* Grid Limits column: stop loss + execution mode + buy depth */}
                {colIdx === 2 && selectedType?.id === 'grid' && (
                  <>
                    <MultiSelectDropdown
                      label="Options"
                      icon={SlidersHorizontal}
                      options={[
                        { value: 'executionMode', label: 'Execution Mode' },
                        { value: 'stopLoss', label: 'Stop Loss' },
                        { value: 'buyDepth', label: 'Buy Depth' },
                        { value: 'sellDepth', label: 'Sell Depth' },
                        { value: 'rebalanceMode', label: 'Auto-Rebalance' },
                      ]}
                      selected={enabledOptions}
                      onChange={setEnabledOptions}
                    />
                    {enabledOptions.includes('stopLoss') && (
                      <div className="flex items-end gap-2">
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                            Stop Loss Mode <InfoTip id="stopLossMode" />
                          </label>
                          <select
                            value={formData.parameters.stopLossMode || 'none'}
                            onChange={(e) => handleParameterChange('stopLossMode', e.target.value)}
                            className={inputClasses}
                          >
                            <option value="none">None (Open-ended)</option>
                            <option value="percentage">Percentage</option>
                          </select>
                        </div>
                        {formData.parameters.stopLossMode === 'percentage' && (
                          <div className="w-[20%]">
                            <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                              % <InfoTip id="stopLossPercent" />
                            </label>
                            <input
                              type="number"
                              value={formData.parameters.stopLossPercent ?? 5}
                              onChange={(e) => handleParameterChange('stopLossPercent', parseFloat(e.target.value))}
                              min={0.1}
                              step={0.1}
                              className={inputClasses}
                              required
                            />
                          </div>
                        )}
                      </div>
                    )}
                    {enabledOptions.includes('executionMode') && (
                      <div>
                        <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                          Execution Mode * <InfoTip id="executionMode" />
                        </label>
                        <select
                          value={formData.parameters.executionMode || 'auto-executor'}
                          onChange={(e) => handleParameterChange('executionMode', e.target.value)}
                          className={inputClasses}
                          required
                        >
                          <option value="auto-executor">Auto-Executor (Continuous)</option>
                          <option value="position-aware">Position-Aware (Startup only)</option>
                        </select>
                      </div>
                    )}
                    {enabledOptions.includes('executionMode') && (
                      <div>
                        <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                          Order Type * <InfoTip id="orderType" />
                        </label>
                        <select
                          value={formData.parameters.orderType || 'limit'}
                          onChange={(e) => handleParameterChange('orderType', e.target.value)}
                          className={inputClasses}
                          required
                        >
                          <option value="limit">Limit Orders</option>
                          <option value="market">Market Orders</option>
                        </select>
                      </div>
                    )}
                    {enabledOptions.includes('buyDepth') && (formData.parameters.orderType || 'limit') !== 'market' && (
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                            Buy Depth Mode <InfoTip id="buyDepthMode" />
                          </label>
                          <select
                            value={formData.parameters.buyDepthMode || 'levels'}
                            onChange={(e) => handleParameterChange('buyDepthMode', e.target.value)}
                            className={inputClasses}
                          >
                            <option value="levels">Grid Levels</option>
                            <option value="percentage">Percentage</option>
                          </select>
                        </div>
                        {(formData.parameters.buyDepthMode || 'levels') === 'levels' ? (
                          <div>
                            <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                              N Levels <InfoTip id="buyDepthN" />
                            </label>
                            <input
                              type="number"
                              value={formData.parameters.buyDepthN ?? 2}
                              onChange={(e) => handleParameterChange('buyDepthN', parseInt(e.target.value, 10))}
                              min={1}
                              max={50}
                              className={inputClasses}
                            />
                          </div>
                        ) : (
                          <div>
                            <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                              Max (%) <InfoTip id="buyDepthPercent" />
                            </label>
                            <input
                              type="number"
                              value={formData.parameters.buyDepthPercent ?? 2}
                              onChange={(e) => handleParameterChange('buyDepthPercent', parseFloat(e.target.value))}
                              min={0.1}
                              max={20}
                              step={0.1}
                              className={inputClasses}
                            />
                          </div>
                        )}
                      </div>
                    )}
                    {enabledOptions.includes('sellDepth') && (formData.parameters.orderType || 'limit') !== 'market' && (
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                            Sell Depth Mode <InfoTip id="sellDepthMode" />
                          </label>
                          <select
                            value={formData.parameters.sellDepthMode || 'levels'}
                            onChange={(e) => handleParameterChange('sellDepthMode', e.target.value)}
                            className={inputClasses}
                          >
                            <option value="levels">Grid Levels</option>
                            <option value="percentage">Percentage</option>
                          </select>
                        </div>
                        {(formData.parameters.sellDepthMode || 'levels') === 'levels' ? (
                          <div>
                            <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                              N Levels <InfoTip id="sellDepthN" />
                            </label>
                            <input
                              type="number"
                              value={formData.parameters.sellDepthN ?? 2}
                              onChange={(e) => handleParameterChange('sellDepthN', parseInt(e.target.value, 10))}
                              min={1}
                              max={50}
                              className={inputClasses}
                            />
                          </div>
                        ) : (
                          <div>
                            <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                              Max (%) <InfoTip id="sellDepthPercent" />
                            </label>
                            <input
                              type="number"
                              value={formData.parameters.sellDepthPercent ?? 2}
                              onChange={(e) => handleParameterChange('sellDepthPercent', parseFloat(e.target.value))}
                              min={0.1}
                              max={20}
                              step={0.1}
                              className={inputClasses}
                            />
                          </div>
                        )}
                      </div>
                    )}
                    {enabledOptions.includes('rebalanceMode') && (
                      <>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                              Auto-Rebalance <InfoTip id="rebalanceMode" />
                            </label>
                            <select
                              value={formData.parameters.rebalanceMode || 'off'}
                              onChange={(e) => {
                                const mode = e.target.value;
                                setFormData((prev) => ({
                                  ...prev,
                                  parameters: {
                                    ...prev.parameters,
                                    rebalanceMode: mode,
                                    autoRebalance: mode !== 'off',
                                  },
                                }));
                              }}
                              className={inputClasses}
                            >
                              <option value="off">Off</option>
                              <option value="fixed">Fixed</option>
                              <option value="dynamic">Dynamic</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                              Cooldown (min) <InfoTip id="rebalanceCooldownMin" />
                            </label>
                            <input
                              type="number"
                              value={formData.parameters.rebalanceCooldownMin ?? 15}
                              onChange={(e) => handleParameterChange('rebalanceCooldownMin', parseInt(e.target.value, 10))}
                              min={1}
                              max={1440}
                              disabled={!formData.parameters.rebalanceMode || formData.parameters.rebalanceMode === 'off'}
                              className={inputClasses}
                            />
                          </div>
                        </div>
                        {formData.parameters.rebalanceMode === 'dynamic' && (
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                                BB Period <InfoTip id="bbPeriod" />
                              </label>
                              <input
                                type="number"
                                value={formData.parameters.bbPeriod ?? 20}
                                onChange={(e) => handleParameterChange('bbPeriod', parseInt(e.target.value, 10))}
                                min={2}
                                max={200}
                                className={inputClasses}
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                                BB Std Dev <InfoTip id="bbStdDev" />
                              </label>
                              <input
                                type="number"
                                value={formData.parameters.bbStdDev ?? 2}
                                onChange={(e) => handleParameterChange('bbStdDev', parseFloat(e.target.value))}
                                min={0.5}
                                max={5}
                                step={0.1}
                                className={inputClasses}
                              />
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            </Card>
            </div>
          ))}

          {/* Column 5: Planned Orders Preview */}
          <div className="flex-1 min-w-[200px] flex min-h-0">
          <Card className="flex-1" scrollable title={`Planned Orders${previewOrders.filter(o => !o.metadata?.simulated).length ? ` (${previewOrders.filter(o => !o.metadata?.simulated).length})` : ''}`}>
            <div className="space-y-1">
              {!isEditMode ? (
                <p className="text-xs text-muted-foreground">Save strategy to preview planned orders.</p>
              ) : previewLoading ? (
                <div className="text-center py-4 text-muted-foreground text-xs">Computing...</div>
              ) : previewOrders.length === 0 ? (
                <p className="text-xs text-muted-foreground">No planned orders.</p>
              ) : (
                <div className="space-y-1">
                  {previewOrders.filter(o => !o.metadata?.simulated).map((order, i) => (
                    <div
                      key={i}
                      className={`flex items-center justify-between text-xs px-2 py-1 rounded ${
                        order.side === 'buy' ? 'bg-green-900/10' : 'bg-red-900/10'
                      }`}
                    >
                      <span className={`font-bold ${order.side === 'buy' ? 'text-green-500' : 'text-red-500'}`}>
                        {order.side.toUpperCase()}
                      </span>
                      <span className="text-muted-foreground">{order.quantity}</span>
                      <span className="text-muted-foreground">
                        {order.limitPrice ? `$${order.limitPrice.toFixed(2)}` : 'MKT'}
                      </span>
                    </div>
                  ))}
                  {previewOrders.some(o => o.metadata?.simulated) && (
                    <>
                      <div className="text-[10px] text-muted-foreground/60 uppercase tracking-wider pt-1 pb-0.5 border-t border-border/40">Scenarios</div>
                      {previewOrders.filter(o => o.metadata?.simulated).map((order, i) => (
                        <div
                          key={`sim-${i}`}
                          className="flex flex-col text-xs px-2 py-1 rounded border-l-2 border-dashed border-amber-500/60 opacity-60 bg-muted/30"
                        >
                          <div className="flex items-center justify-between">
                            <span className={`font-bold italic ${order.side === 'buy' ? 'text-green-500' : 'text-red-500'}`}>
                              {order.side.toUpperCase()} <span className="text-amber-500 font-normal">(Scenario)</span>
                            </span>
                            <span className="text-muted-foreground">{order.quantity}</span>
                            <span className="text-muted-foreground">
                              {order.limitPrice ? `$${order.limitPrice.toFixed(2)}` : 'MKT'}
                            </span>
                          </div>
                          {order.metadata?.scenario && (
                            <span className="text-[10px] text-muted-foreground/70 mt-0.5">{order.metadata.scenario}</span>
                          )}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          </Card>
          </div>
        </div>
      </form>
      )}
      </div>

      {/* Validation Modal */}
      <ValidationModal
        open={validationModal.open}
        parameterErrors={validationModal.parameterErrors}
        capitalError={validationModal.capitalError}
        onUpdate={() => setValidationModal({ open: false, parameterErrors: [], capitalError: null })}
        onClose={() => {
          setValidationModal({ open: false, parameterErrors: [], capitalError: null });
          if (!isEditMode) navigate('/strategies/manager');
        }}
      />

      {/* Notification Preferences Modal */}
      <NotificationPrefsModal
        open={notifModalOpen}
        onClose={() => setNotifModalOpen(false)}
        prefs={notificationPrefs}
        onPrefsChange={setNotificationPrefs}
      />

      {/* Import confirmation dialog */}
      {importConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-bold text-foreground mb-2">Import Strategy</h3>
            <p className="text-sm text-muted-foreground mb-4">
              This will overwrite the current strategy configuration. Continue?
            </p>
            <div className="flex items-center justify-end gap-3">
              <Button variant="ghost" onClick={() => setImportConfirm(null)}>
                Cancel
              </Button>
              <Button className="bg-cyan-600 hover:bg-cyan-700 text-white" onClick={handleImportConfirm}>
                Import
              </Button>
            </div>
          </div>
        </div>
      )}
    </PageTransition>
  );
}

export default StrategyBuilder;
